// Workforce › Templates (`/c/{slug}/agents/templates`; TEMPLATES-T2..T4, design
// docs/design/2026-10-28-agent-templates.md §6): browse the role templates a DE is deployed from,
// and the company's overlay over them. Tabs: Roles · Overlay (Skills and Updates are later
// slices). A role card opens a preview drawer (`roleTemplates.plan`: every layer merged) with
// Deploy, which opens the Add-DE wizard with the role picked. Managers only (the API's rule).
import {
  Badge,
  buttonVariants,
  cn,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerSection,
  DrawerTitle,
  InfoTip,
  LocationHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TabsContent,
  ViewTabs,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { LayoutTemplateIcon, RocketIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { useCurrency } from "../agents/agent-parts";
import type { CompanyAccess } from "../company";
import { LoadError, Loading, Nothing } from "../screen-parts";
import { DENSE_TABLE } from "../settings/row-actions";
import { describeSchedule } from "../structure/rrule";
import { OverlayTab } from "./overlay-tab";
import {
  FAMILY_LABELS,
  formatMinor,
  humanKey,
  LAYER_LABELS,
  levelText,
  type RoleCard,
  rolePlanQuery,
  roleTemplatesQuery,
  TIER_LABELS,
  type VerticalPack,
} from "./role-model";

export const TEMPLATE_TABS = ["roles", "overlay"] as const;
export type TemplateTab = (typeof TEMPLATE_TABS)[number];
const TAB_LABELS: Record<TemplateTab, string> = { roles: "Roles", overlay: "Overlay" };

export function TemplatesScreen({
  api,
  company,
  tab,
  onTab,
  roleId,
  onRole,
}: {
  api: Api;
  company: CompanyAccess;
  tab: TemplateTab;
  onTab: (tab: TemplateTab) => void;
  /** The role whose preview drawer is open (`?role=`). */
  roleId: string | undefined;
  onRole: (id: string | null) => void;
}) {
  const co = company.id;
  const list = useQuery(roleTemplatesQuery(api, co));
  const roles = list.data?.data ?? [];
  const packs = list.data?.vertical_packs ?? [];
  const open = roleId ? roles.find((r) => r.template.id === roleId) : undefined;
  return (
    <div className="flex min-w-0 flex-1 flex-col" data-slot="templates-screen">
      <LocationHeader
        icon={LayoutTemplateIcon}
        title="Templates"
        {...(list.isSuccess ? { count: roles.length } : {})}
      />
      <div className="flex min-w-0 flex-col gap-4 px-6 py-4 max-md:px-4">
        <ViewTabs
          aria-label="Templates"
          value={tab}
          onValueChange={(v) => onTab(v as TemplateTab)}
          tabs={TEMPLATE_TABS.map((t) => ({ value: t, label: TAB_LABELS[t] }))}
        >
          <TabsContent value="roles" className="min-w-0 pt-4">
            {list.isPending ? (
              <Loading label="Loading role templates" rows={3} />
            ) : list.isError ? (
              <LoadError what="Role templates" error={list.error} />
            ) : roles.length === 0 ? (
              <Nothing title="No role templates published yet" />
            ) : (
              <ul
                className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
                data-slot="role-cards"
              >
                {roles.map((r) => (
                  <li key={r.template.id}>
                    <RoleCardView role={r} packs={packs} onOpen={() => onRole(r.template.id)} />
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
          <TabsContent value="overlay" className="min-w-0 pt-4">
            <OverlayTab api={api} company={company} roles={roles} packs={packs} />
          </TabsContent>
        </ViewTabs>
      </div>
      {open ? (
        <RoleDrawer api={api} company={company} role={open} onClose={() => onRole(null)} />
      ) : null}
    </div>
  );
}

/** A short labelled line of chips on a card ("Needs: Mail, CRM"). */
function ChipLine({ label, items }: { label: string; items: ReactNode[] }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs">
      <span className="mr-0.5 text-muted-foreground">{label}</span>
      {items}
    </div>
  );
}

export function RoleCardView({
  role,
  packs,
  onOpen,
}: {
  role: RoleCard;
  packs: VerticalPack[];
  onOpen: () => void;
}) {
  const v = role.version;
  const packName = (key: string) => packs.find((p) => p.key === key)?.name ?? key;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-role={role.template.key}
      aria-label={`Preview ${role.template.name}`}
      className="flex h-full w-full flex-col gap-2 rounded-lg border border-border bg-card p-4 text-left transition-colors hover:bg-muted/40 focus-visible:outline-2 focus-visible:outline-ring"
    >
      <span className="flex items-start justify-between gap-2">
        <span className="font-medium">{role.template.name}</span>
        {v.model_tier ? <Badge variant="outline">{TIER_LABELS[v.model_tier]}</Badge> : null}
      </span>
      {role.summary ? (
        <span className="line-clamp-2 text-sm text-muted-foreground">{role.summary}</span>
      ) : null}
      <ChipLine
        label="Skills"
        items={role.skills.map((s) => (
          <Badge key={s} variant="neutral">
            {s}
          </Badge>
        ))}
      />
      <ChipLine
        label="Needs"
        items={(v.required_connections ?? []).map((c) => (
          <Badge key={c.family} variant="outline">
            {FAMILY_LABELS[c.family]}
          </Badge>
        ))}
      />
      <ChipLine
        label="KPIs"
        items={(v.kpis ?? []).map((k) => (
          <Badge key={k.key} variant="outline">
            {humanKey(k.key)}
            {k.target !== null ? ` ${k.direction === "lte" ? "≤" : "≥"} ${k.target}` : ""}
          </Badge>
        ))}
      />
      <ChipLine
        label="Packs"
        items={role.vertical_packs.map((k) => (
          <Badge key={k} variant="accent">
            {packName(k)}
          </Badge>
        ))}
      />
      <span className="mt-auto text-xs text-muted-foreground" data-slot="in-use">
        In use: {role.in_use}
      </span>
    </button>
  );
}

/** The role's preview: what a deploy gives a DE, every layer merged. */
export function RoleDrawer({
  api,
  company,
  role,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  role: RoleCard;
  onClose: () => void;
}) {
  const plan = useQuery(rolePlanQuery(api, company.id, role.template.id));
  const currency = useCurrency(api, company.id) ?? "USD";
  const p = plan.data;
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()} width="640px">
      <DrawerHeader
        closeLabel="Close the preview"
        actions={
          <Link
            to="/c/$slug/setup/des/new"
            params={{ slug: company.slug }}
            search={(prev: Record<string, unknown>) => ({
              ...keepFocus(prev),
              role: role.template.id,
            })}
            className={cn(buttonVariants({ size: "sm" }))}
            data-slot="role-deploy"
          >
            <RocketIcon aria-hidden="true" />
            Deploy
          </Link>
        }
      >
        Templates › Roles
      </DrawerHeader>
      <DrawerBody data-slot="role-drawer">
        <DrawerTitle>{role.template.name}</DrawerTitle>
        {plan.isPending ? (
          <div className="mt-4">
            <Loading label="Loading the role" rows={4} />
          </div>
        ) : plan.isError ? (
          <div className="mt-4">
            <LoadError what="The role" error={plan.error} />
          </div>
        ) : p ? (
          <>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline">{TIER_LABELS[p.model_tier]}</Badge>
              <span>{formatMinor(p.budget_minor, currency)} / month</span>
              <span>
                Probation: {p.probation.tasks} tasks, {p.probation.days} days
              </span>
            </div>
            {p.problems.length ? (
              <ul className="mt-3 flex flex-col gap-1 text-xs text-warning-text">
                {p.problems.map((x) => (
                  <li key={`${x.path}:${x.code}`}>{x.message}</li>
                ))}
              </ul>
            ) : null}
            <DrawerSection title="Instructions" className="mt-6" data-slot="plan-instructions">
              {p.instructions.length ? (
                <ol className="flex flex-col gap-3">
                  {p.instructions.map((b, i) => (
                    <li
                      // biome-ignore lint/suspicious/noArrayIndexKey: the layer order is fixed
                      key={i}
                      data-layer={b.layer}
                      className="rounded-md border border-border p-3"
                    >
                      <div className="mb-1 flex flex-wrap items-center gap-2 text-xs">
                        <Badge variant="neutral">{LAYER_LABELS[b.layer] ?? b.layer}</Badge>
                        <span className="font-medium">{b.label}</span>
                        <span className="text-muted-foreground">{b.version}</span>
                      </div>
                      <p className="line-clamp-6 text-sm whitespace-pre-wrap">{b.text}</p>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm text-muted-foreground">None</p>
              )}
            </DrawerSection>
            <DrawerSection
              title={
                <span className="inline-flex items-center gap-1">
                  Access
                  <InfoTip label="About access">
                    Cap: the overlay's ceiling. Now: what applies during probation (at most With
                    approval).
                  </InfoTip>
                </span>
              }
            >
              <Table className={DENSE_TABLE} data-slot="plan-access">
                <TableHeader>
                  <TableRow>
                    <TableHead>Family</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Cap</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Now</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {p.access.map((a) => (
                    <TableRow key={a.family} data-family={a.family}>
                      <TableCell>{FAMILY_LABELS[a.family]}</TableCell>
                      <TableCell>{levelText(a.role)}</TableCell>
                      <TableCell>{a.cap ? levelText(a.cap) : "—"}</TableCell>
                      <TableCell>{levelText(a.level)}</TableCell>
                      <TableCell>{levelText(a.now)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </DrawerSection>
            <DrawerSection title="KPIs">
              {p.kpis.length ? (
                <ul className="flex flex-col gap-1 text-sm" data-slot="plan-kpis">
                  {p.kpis.map((k) => (
                    <li key={k.key} className="flex flex-wrap items-center gap-2">
                      <span>{k.name}</span>
                      <span className="text-muted-foreground">
                        {k.target === null
                          ? "No target"
                          : `${k.direction === "lte" ? "≤" : "≥"} ${k.target}`}
                      </span>
                      <Badge variant="outline">{LAYER_LABELS[k.source] ?? k.source}</Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">None</p>
              )}
            </DrawerSection>
            <DrawerSection title="Cadence">
              {p.cadence.length ? (
                <ul className="flex flex-col gap-1 text-sm" data-slot="plan-cadence">
                  {p.cadence.map((c) => (
                    <li key={c.key}>
                      {c.name}{" "}
                      <span className="text-muted-foreground">· {describeSchedule(c.rrule)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">None</p>
              )}
            </DrawerSection>
            <DrawerSection title="Connections">
              {p.required_connections.length ? (
                <ul className="flex flex-col gap-1 text-sm" data-slot="plan-connections">
                  {p.required_connections.map((c) => (
                    <li key={c.family} className="flex flex-wrap items-center gap-2">
                      <span>{FAMILY_LABELS[c.family]}</span>
                      <ConnectionBadge status={c.status} />
                      {c.why ? (
                        <InfoTip label={`Why ${FAMILY_LABELS[c.family]}`}>{c.why}</InfoTip>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">None needed</p>
              )}
            </DrawerSection>
          </>
        ) : null}
      </DrawerBody>
    </Drawer>
  );
}

export function ConnectionBadge({ status }: { status: "connected" | "missing" | "not_available" }) {
  return status === "connected" ? (
    <Badge variant="success">Connected</Badge>
  ) : status === "missing" ? (
    <Badge variant="warning">Not connected</Badge>
  ) : (
    <Badge variant="neutral">No connector yet</Badge>
  );
}
